// src/services/chat/messageService.js

import { auth, db } from '../../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  writeBatch,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  increment,
  Timestamp,
  onSnapshot,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { chatLocalDb } from './chatLocalDb';
import { ChatMessage, MessageStatus, MessageType } from '../../models/chatModels/chatMessage';

// ── Presence record returned to the UI ──────────────────────────────────────

export class UserPresence {
  constructor({ isOnline, lastSeenAt }) {
    this.isOnline = isOnline;
    this.lastSeenAt = lastSeenAt || null;
  }
}

class FirebaseMessageService {
  // ── Singleton ──────────────────────────────────────────────────────────────

  constructor() {
    this._db = db;
    this._auth = auth;
    this._localDb = chatLocalDb;
    this._presenceTimer = null;
    this._presenceStarted = false;
  }

  static getInstance() {
    if (!FirebaseMessageService._instance) {
      FirebaseMessageService._instance = new FirebaseMessageService();
    }
    return FirebaseMessageService._instance;
  }

  get currentUserId() {
    return this._auth.currentUser?.uid || '';
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  _chatRef(conversationId) {
    return doc(this._db, 'chats', conversationId);
  }

  _msgRef(conversationId, messageId) {
    return doc(this._db, 'chats', conversationId, 'messages', messageId);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 1. MESSAGE STREAM
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Real-time stream of messages for `conversationId`, ordered oldest→newest.
   * Mirrors the Flutter Stream<List<ChatMessage>> — `callback` fires on every
   * snapshot (initial + subsequent updates). Returns an unsubscribe function.
   */
  messagesStream(conversationId, callback) {
    const messagesRef = collection(this._db, 'chats', conversationId, 'messages');
    const q = query(messagesRef, orderBy('ts', 'asc'));

    return onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs
          .map((docSnap) => this._messageFromDoc(docSnap, conversationId))
          .filter((m) => m !== null);
        callback(messages);
      },
      (error) => {
        console.error('messagesStream error:', error);
      }
    );
  }

  // Kept as an alias for any existing callers using the old name.
  messagesStreamRealtime(conversationId, callback) {
    return this.messagesStream(conversationId, callback);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 2. SEND MESSAGE
  // ─────────────────────────────────────────────────────────────────────────

  async sendMessage({
    conversationId,
    receiverId,
    content,
    messageType = MessageType.TEXT,
    attachmentUrl = null,
    attachmentType = null,
    attachmentName = null,
    attachmentSize = null,
    audioDuration = null,
    imageWidth = null,
    imageHeight = null,
    replyMetadata = null,
    isForwarded = false,
  }) {
    const myId = this.currentUserId;
    if (!myId) throw new Error('Not authenticated');

    const messageId = doc(collection(this._db, 'chats', conversationId, 'messages')).id;
    const now = new Date();

    const message = new ChatMessage({
      id: messageId,
      conversationId: conversationId,
      senderId: myId,
      receiverId: receiverId,
      content: content,
      timestamp: now,
      status: MessageStatus.SENDING,
      messageType: messageType,
      attachmentUrl: attachmentUrl,
      attachmentType: attachmentType,
      attachmentName: attachmentName,
      attachmentSize: attachmentSize,
      audioDuration: audioDuration,
      imageWidth: imageWidth,
      imageHeight: imageHeight,
      replyToMessageId: replyMetadata?.replyToId || null,
      replyToContent: replyMetadata?.replyToContent || null,
      replyToSenderId: replyMetadata?.replyToSenderId || null,
      replyToType: replyMetadata?.replyToType || null,
      replyToAttachmentUrl: replyMetadata?.replyToAttachmentUrl || null,
      isForwarded: isForwarded,
      seenBy: [myId],
    });

    // Optimistic local save
    await this._localDb.saveMessage(message);

    try {
      const convRef = this._chatRef(conversationId);
      const msgRef = this._msgRef(conversationId, messageId);
      const batch = writeBatch(this._db);

      // Build message data
      const msgData = {
        from: myId,
        to: receiverId,
        msg: content,
        ts: Timestamp.fromDate(now),
        msgType: messageType,
        status: 'sent',
        isForwarded: isForwarded,
        seenBy: [myId],
        isDeleted: false,
        deletedFor: [],
      };

      if (attachmentUrl) msgData.attachmentUrl = attachmentUrl;
      if (attachmentType) msgData.attachmentType = attachmentType;
      if (attachmentName) msgData.attachmentName = attachmentName;
      if (attachmentSize) msgData.attachmentSize = attachmentSize;
      if (audioDuration) msgData.audioDuration = audioDuration;
      if (imageWidth) msgData.imageWidth = imageWidth;
      if (imageHeight) msgData.imageHeight = imageHeight;

      if (replyMetadata) {
        msgData.replyToMessageId = replyMetadata.replyToId;
        if (replyMetadata.replyToContent) msgData.replyToContent = replyMetadata.replyToContent;
        if (replyMetadata.replyToSenderId) msgData.replyToSenderId = replyMetadata.replyToSenderId;
        if (replyMetadata.replyToType) msgData.replyToType = replyMetadata.replyToType;
        if (replyMetadata.replyToAttachmentUrl) msgData.replyToAttachmentUrl = replyMetadata.replyToAttachmentUrl;
      }

      batch.set(msgRef, msgData);

      // Update conversation document
      // NOTE: arrayUnion/arrayRemove take individual elements as rest args in
      // the JS SDK — NOT an array of elements (that's the Dart API's shape).
      batch.set(
        convRef,
        {
          participants: arrayUnion(myId, receiverId),
          deletedBy: arrayRemove(myId),
          lastMessage: this._lastMessagePreview(content, messageType),
          lastMessageSender: myId,
          lastMessageTime: serverTimestamp(),
          lastMessageType: messageType,
          lastMessageStatus: 'sent',
          [`unreadCount.${receiverId}`]: increment(1),
          [`unreadCount.${myId}`]: 0,
        },
        { merge: true }
      );

      await batch.commit();

      // Update local status to sent
      const sentMsg = { ...message, status: MessageStatus.SENT };
      await this._localDb.saveMessage(sentMsg);
      await this._localDb.updateConversationLastMessageStatus(conversationId, MessageStatus.SENT);

      // Send message notification
      if (receiverId !== myId) {
        try {
          const { sendMessageNotification } = await import('../notificationService');
          await sendMessageNotification({
            recipientUid: receiverId,
            conversationId: conversationId,
            messageText: content,
          });
        } catch (error) {
          console.error('Failed to send message notification:', error);
        }
      }

      this._checkAndMarkDelivered(conversationId, messageId, receiverId);

      return sentMsg;
    } catch (error) {
      console.error('sendMessage error:', error);
      const failedMsg = { ...message, status: MessageStatus.FAILED };
      await this._localDb.saveMessage(failedMsg);
      throw error;
    }
  }

  _checkAndMarkDelivered(conversationId, messageId, receiverId) {
    getDoc(doc(this._db, 'presence', receiverId))
      .then((docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const isOnline = data.isOnline || false;
          if (isOnline) {
            this.markMessageDelivered(conversationId, messageId);
          }
        }
      })
      .catch((error) => console.error('checkDelivered error:', error));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 3. MESSAGE STATUS UPDATES
  // ─────────────────────────────────────────────────────────────────────────

  async markMessageDelivered(conversationId, messageId) {
    try {
      const msgRef = this._msgRef(conversationId, messageId);
      await updateDoc(msgRef, {
        status: 'delivered',
        seenBy: arrayUnion(this._auth.currentUser?.uid || ''),
      });
      await this._localDb.updateMessageStatus(messageId, MessageStatus.DELIVERED);
    } catch (error) {
      console.error('markDelivered error:', error);
    }
  }

  async markMessageAsRead(conversationId, messageId) {
    const myId = this.currentUserId;
    if (!myId) return;

    try {
      const msgRef = this._msgRef(conversationId, messageId);
      await updateDoc(msgRef, {
        status: 'read',
        seenBy: arrayUnion(myId),
      });
      await this._localDb.updateMessageStatus(messageId, MessageStatus.READ);
    } catch (error) {
      console.error('markMessageAsRead error:', error);
    }
  }

  async markConversationRead(conversationId) {
    const myId = this.currentUserId;
    if (!myId) return;

    try {
      // Find all unread messages sent TO me
      const messagesRef = collection(this._db, 'chats', conversationId, 'messages');
      const q = query(
        messagesRef,
        where('to', '==', myId),
        where('status', 'in', ['sent', 'delivered'])
      );
      const snap = await getDocs(q);

      if (!snap.empty) {
        const batch = writeBatch(this._db);
        for (const docSnap of snap.docs) {
          batch.update(docSnap.ref, {
            status: 'read',
            seenBy: arrayUnion(myId),
          });
          await this._localDb.updateMessageStatus(docSnap.id, MessageStatus.READ);
        }
        await batch.commit();
      }

      // Reset unread count on conversation
      await setDoc(
        this._chatRef(conversationId),
        {
          [`unreadCount.${myId}`]: 0,
          [`lastSeen.${myId}`]: serverTimestamp(),
          lastMessageStatus: 'read',
        },
        { merge: true }
      );

      await this._localDb.markConversationRead(conversationId);
    } catch (error) {
      console.error('markConversationRead error:', error);
    }
  }

  async getUnreadCount(conversationId) {
    const myId = this.currentUserId;
    if (!myId) return 0;

    try {
      const docSnap = await getDoc(this._chatRef(conversationId));
      if (!docSnap.exists()) return 0;

      const data = docSnap.data();
      const unreadMap = data.unreadCount || {};
      return unreadMap[myId] || 0;
    } catch (error) {
      console.error('getUnreadCount error:', error);
      return 0;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 4. STATUS STREAM
  // ─────────────────────────────────────────────────────────────────────────

  myMessageStatusStream(conversationId, callback) {
    const myId = this.currentUserId;
    const messagesRef = collection(this._db, 'chats', conversationId, 'messages');
    const q = query(messagesRef, where('from', '==', myId));

    return onSnapshot(
      q,
      (snapshot) => {
        const map = {};
        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          const status = this._statusFromString(data.status);
          map[docSnap.id] = status;
        }
        callback(map);
      },
      (error) => {
        console.error('myMessageStatusStream error:', error);
      }
    );
  }

  newMessageStream(conversationId, callback) {
    const myId = this.currentUserId;
    const messagesRef = collection(this._db, 'chats', conversationId, 'messages');
    const q = query(
      messagesRef,
      where('to', '==', myId),
      where('status', 'in', ['sent', 'delivered'])
    );

    return onSnapshot(
      q,
      (snapshot) => {
        if (snapshot.empty) {
          callback(null);
          return;
        }
        const latestDoc = snapshot.docs[snapshot.docs.length - 1];
        const message = this._messageFromDoc(latestDoc, conversationId);
        callback(message);
      },
      (error) => {
        console.error('newMessageStream error:', error);
      }
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 5. PRESENCE (Online/Offline tracking)
  // ─────────────────────────────────────────────────────────────────────────

  startPresence() {
    if (this._presenceStarted) return;
    this._presenceStarted = true;
    this._writePresence(true);
    this._presenceTimer = setInterval(() => {
      this._writePresence(true);
    }, 30000);
  }

  async goOffline() {
    if (this._presenceTimer) {
      clearInterval(this._presenceTimer);
      this._presenceTimer = null;
    }
    this._presenceStarted = false;
    await this._writePresence(false);
  }

  async _writePresence(online) {
    const myId = this.currentUserId;
    if (!myId) return;

    try {
      await setDoc(
        doc(this._db, 'presence', myId),
        {
          isOnline: online,
          lastSeenAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          uid: myId,
        },
        { merge: true }
      );
    } catch (error) {
      console.error('_writePresence error:', error);
    }
  }

  presenceStream(uid, callback) {
    return onSnapshot(
      doc(this._db, 'presence', uid),
      (docSnap) => {
        if (!docSnap.exists()) {
          callback(new UserPresence({ isOnline: false }));
          return;
        }
        const data = docSnap.data();
        const isOnline = data.isOnline || false;
        let lastSeen = null;
        if (data.lastSeenAt?.toDate) {
          lastSeen = data.lastSeenAt.toDate();
        }
        callback(new UserPresence({ isOnline, lastSeenAt: lastSeen }));
      },
      (error) => {
        console.error('presenceStream error:', error);
      }
    );
  }

  async fetchPresence(uid) {
    try {
      const docSnap = await getDoc(doc(this._db, 'presence', uid));
      if (!docSnap.exists()) {
        return new UserPresence({ isOnline: false });
      }
      const data = docSnap.data();
      const isOnline = data.isOnline || false;
      let lastSeen = null;
      if (data.lastSeenAt?.toDate) {
        lastSeen = data.lastSeenAt.toDate();
      }
      return new UserPresence({ isOnline, lastSeenAt: lastSeen });
    } catch (error) {
      console.error('fetchPresence error:', error);
      return new UserPresence({ isOnline: false });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 6. DELIVERED ON OPEN
  // ─────────────────────────────────────────────────────────────────────────

  async markIncomingDelivered(conversationId) {
    const myId = this.currentUserId;
    if (!myId) return;

    try {
      const messagesRef = collection(this._db, 'chats', conversationId, 'messages');
      const q = query(
        messagesRef,
        where('to', '==', myId),
        where('status', '==', 'sent')
      );
      const snap = await getDocs(q);

      if (snap.empty) return;

      const batch = writeBatch(this._db);
      for (const docSnap of snap.docs) {
        batch.update(docSnap.ref, {
          status: 'delivered',
          seenBy: arrayUnion(myId),
        });
      }
      await batch.commit();
    } catch (error) {
      console.error('markIncomingDelivered error:', error);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 7. DISPOSE
  // ─────────────────────────────────────────────────────────────────────────

  async dispose() {
    await this.goOffline();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  _messageFromDoc(docSnap, conversationId) {
    try {
      const data = docSnap.data();
      if (!data) return null;

      let timestamp;
      const ts = data.ts;
      if (ts?.toDate) {
        timestamp = ts.toDate();
      } else if (typeof ts === 'string') {
        timestamp = new Date(ts);
      } else {
        timestamp = new Date();
      }

      return new ChatMessage({
        id: docSnap.id,
        conversationId: conversationId,
        senderId: data.from || '',
        receiverId: data.to || '',
        content: data.msg || '',
        timestamp: timestamp,
        status: this._statusFromString(data.status),
        messageType: this._typeFromString(data.msgType),
        attachmentUrl: data.attachmentUrl || null,
        attachmentType: data.attachmentType || null,
        attachmentName: data.attachmentName || null,
        attachmentSize: data.attachmentSize || null,
        audioDuration: data.audioDuration || null,
        imageWidth: data.imageWidth || null,
        imageHeight: data.imageHeight || null,
        replyToMessageId: data.replyToMessageId || null,
        replyToContent: data.replyToContent || null,
        replyToSenderId: data.replyToSenderId || null,
        replyToType: data.replyToType || null,
        replyToAttachmentUrl: data.replyToAttachmentUrl || null,
        isForwarded: data.isForwarded || false,
        seenBy: data.seenBy || [],
        deletedFor: data.deletedFor || null,
        isDeleted: data.isDeleted || false,
      });
    } catch (error) {
      console.error('_messageFromDoc error:', error);
      return null;
    }
  }

  _statusFromString(v) {
    switch (v) {
      case 'sent':
        return MessageStatus.SENT;
      case 'delivered':
        return MessageStatus.DELIVERED;
      case 'read':
        return MessageStatus.READ;
      case 'failed':
        return MessageStatus.FAILED;
      default:
        return MessageStatus.SENDING;
    }
  }

  _typeFromString(v) {
    switch (v) {
      case 'image':
        return MessageType.IMAGE;
      case 'audio':
        return MessageType.AUDIO;
      case 'video':
        return MessageType.VIDEO;
      case 'file':
        return MessageType.FILE;
      case 'deleted':
        return MessageType.DELETED;
      default:
        return MessageType.TEXT;
    }
  }

  _lastMessagePreview(content, type) {
    switch (type) {
      case MessageType.IMAGE:
        return '📷 Photo';
      case MessageType.AUDIO:
        return '🎤 Voice message';
      case MessageType.VIDEO:
        return '🎥 Video';
      case MessageType.FILE:
        return `📎 ${content || 'File'}`;
      case MessageType.DELETED:
        return 'This message was deleted';
      default:
        return content;
    }
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────

const firebaseMessageService = FirebaseMessageService.getInstance();
export { firebaseMessageService };
export default firebaseMessageService;